/**
 * TemplatesList - Global template management list page
 *
 * Features:
 * - Grid of template cards
 * - Category and visibility filters
 * - Search
 * - Create template button
 * - Preview modal
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { cn, Button } from '@respectly/ui';
import { Plus, FileText, RefreshCw } from 'lucide-react';
import { useTemplatesStore, useActiveFiltersCount } from '../../stores/useTemplatesStore';
import {
  TemplateCardGrid,
  TemplateFilters,
  TemplatePreview,
} from '../../components/templates';
import type { GlobalTemplate, TemplateCategory } from '@respectly/shared-types';

// ============================================
// Mock Data (until backend is ready)
// ============================================

const MOCK_TEMPLATES: GlobalTemplate[] = [
  {
    id: 'tmpl-1',
    name: 'Welcome Email',
    type: 'email' as TemplateCategory,
    category: null,
    content: 'Welcome to our platform! We\'re excited to have you on board.\n\nHere\'s what you can do next:\n1. Complete your profile\n2. Explore our features\n3. Reach out if you need help\n\nBest regards,\nThe Team',
    visibility: 'all',
    visibleToPlans: null,
    visibleToTenants: null,
    usageCount: 156,
    isActive: true,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: 'tmpl-2',
    name: 'Payment Reminder SMS',
    type: 'sms' as TemplateCategory,
    category: null,
    content: 'Hi {{name}}, your payment of {{amount}} is due on {{date}}. Please complete payment to avoid service interruption.',
    visibility: 'plans',
    visibleToPlans: ['plan-2', 'plan-3'],
    visibleToTenants: null,
    usageCount: 89,
    isActive: true,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-02-10'),
    updatedAt: new Date('2024-02-10'),
  },
  {
    id: 'tmpl-3',
    name: 'Contract Agreement',
    type: 'document' as TemplateCategory,
    category: null,
    content: 'AGREEMENT\n\nThis agreement is entered into between {{company_name}} ("Company") and {{client_name}} ("Client") on {{date}}.\n\n1. SERVICES\n2. PAYMENT TERMS\n3. CONFIDENTIALITY\n4. TERMINATION\n\nSignatures:\n_________________\n_________________',
    visibility: 'all',
    visibleToPlans: null,
    visibleToTenants: null,
    usageCount: 234,
    isActive: true,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-04-15'),
  },
  {
    id: 'tmpl-4',
    name: 'Feedback Survey',
    type: 'form' as TemplateCategory,
    category: null,
    content: '{"title":"Customer Feedback","fields":[{"type":"rating","label":"Overall Satisfaction"},{"type":"text","label":"What did you like most?"},{"type":"text","label":"What can we improve?"}]}',
    visibility: 'tenants',
    visibleToPlans: null,
    visibleToTenants: ['tenant-1', 'tenant-2'],
    usageCount: 45,
    isActive: true,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: 'tmpl-5',
    name: 'Legacy Newsletter',
    type: 'email' as TemplateCategory,
    category: null,
    content: 'Monthly Newsletter\n\nDear Subscriber,\n\nHere are this month\'s updates...',
    visibility: 'none',
    visibleToPlans: null,
    visibleToTenants: null,
    usageCount: 12,
    isActive: false,
    createdById: null,
    createdBy: null,
    createdAt: new Date('2023-06-01'),
    updatedAt: new Date('2023-12-01'),
  },
];

// ============================================
// Component
// ============================================

export function TemplatesList() {
  const navigate = useNavigate();
  const [previewTemplate, setPreviewTemplate] = useState<GlobalTemplate | null>(null);

  const {
    templates,
    filters,
    isLoadingList,
    loadTemplates,
    setFilter,
    clearFilters,
    duplicateTemplate,
    deleteTemplate,
  } = useTemplatesStore();

  const activeFiltersCount = useActiveFiltersCount();

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Use mock data if real data isn't loaded
  const displayTemplates = templates.length > 0 ? templates : MOCK_TEMPLATES;

  const handleCreateTemplate = () => {
    navigate({ to: '/templates/new' });
  };

  const handleEditTemplate = (template: GlobalTemplate) => {
    navigate({ to: '/templates/$id/edit', params: { id: template.id } });
  };

  const handlePreviewTemplate = (template: GlobalTemplate) => {
    setPreviewTemplate(template);
  };

  const handleDuplicateTemplate = async (template: GlobalTemplate) => {
    await duplicateTemplate(template.id);
  };

  const handleDeleteTemplate = async (template: GlobalTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      await deleteTemplate(template.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Global Templates
              </h1>
              <p className="text-sm text-slate-500">
                Manage templates available to your tenants
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadTemplates()}
              disabled={isLoadingList}
            >
              <RefreshCw className={cn('h-4 w-4', isLoadingList && 'animate-spin')} />
            </Button>
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 lg:px-6 pb-4">
          <TemplateFilters
            filters={filters}
            onFilterChange={setFilter}
            onClearFilters={clearFilters}
            activeFiltersCount={activeFiltersCount}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <TemplateCardGrid
          templates={displayTemplates}
          isLoading={isLoadingList}
          onEdit={handleEditTemplate}
          onPreview={handlePreviewTemplate}
          onDuplicate={handleDuplicateTemplate}
          onDelete={handleDeleteTemplate}
          onCreateNew={handleCreateTemplate}
        />
      </div>

      {/* Preview Modal */}
      <TemplatePreview
        template={previewTemplate}
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
      />
    </motion.div>
  );
}
